import { Box, Chip, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import HubIcon from '@mui/icons-material/Hub';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PlaceIcon from '@mui/icons-material/Place';
import type { RootState } from '../../../store';
import { useAdminListarLojasDaRedeQuery, useAdminListarRedesQuery } from '../../../api';
import { AoEntrar, CartaoPonto, Grade, PainelDestaque } from './comuns';

/**
 * Tela da "Rede de lojas" para empresa FRANQUEADA (módulo 76.3).
 *
 * Ela não monta rede — quem monta é a matriz. Então a tela é informativa, com
 * dois estados: a rede ainda não existe (o mais comum no começo) ou a loja já
 * participa de uma. Ver `docs/specs/cupom-rede.md` §7.
 */
export function RedeDoFranqueado(): JSX.Element {
  const { data: redes, isLoading } = useAdminListarRedesQuery();
  const rede = redes?.[0] ?? null;
  const { data: lojas } = useAdminListarLojasDaRedeQuery(rede?.id as number, { skip: !rede });

  if (isLoading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  return rede == null ? <AguardandoRede /> : <ParticipaDaRede nome={rede.nome} lojas={lojas ?? []} />;
}

// ---------------------------------------------------------------------------
// A. A matriz ainda não montou a rede
// ---------------------------------------------------------------------------

function AguardandoRede(): JSX.Element {
  const tema = useTheme();
  const acento = tema.palette.primary.main;
  const escuro = tema.palette.mode === 'dark';

  return (
    <Box>
      <AoEntrar atraso={0}>
        <PainelDestaque>
          <Stack spacing={2} sx={{ maxWidth: 660 }}>
            <Chip
              size="small"
              icon={<HourglassEmptyIcon />}
              label="Aguardando a matriz"
              sx={{
                alignSelf: 'flex-start',
                bgcolor: alpha(acento, escuro ? 0.18 : 0.1),
                color: acento,
                fontWeight: 600,
                '& .MuiChip-icon': { color: acento },
              }}
            />
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.25, fontSize: { xs: 22, md: 28 } }}>
              Sua loja ainda não faz parte de uma rede
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: { md: 17 } }}>
              Sua empresa está vinculada a uma franquia no Sischef, mas a <strong>rede de lojas</strong> do
              cardápio ainda não foi montada. Quem cria a rede e escolhe quais lojas participam é{' '}
              <strong>quem administra a franquia</strong> — não há nada para configurar aqui.
            </Typography>
          </Stack>
        </PainelDestaque>
      </AoEntrar>

      <AoEntrar atraso={140}>
        <Box sx={{ mt: { xs: 3, md: 4 } }}>
          <Typography variant="h6" gutterBottom>
            O que é a rede de lojas
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2, maxWidth: 780 }}>
            É o agrupamento das lojas do grupo dentro do cardápio. Ele serve para a matriz lançar uma
            promoção única, válida em todas as unidades — e que cada cliente só consegue usar{' '}
            <strong>uma vez na rede inteira</strong>, não uma vez em cada loja.
          </Typography>
          <Grade>
            <CartaoPonto
              icon={<LocalOfferIcon />}
              titulo="Um cupom vale em todas"
              texto="A matriz cria um código só. Ele passa a ser aceito no seu cardápio automaticamente, sem você cadastrar nada."
            />
            <CartaoPonto
              icon={<HubIcon />}
              titulo="Uma vez por cliente"
              texto="Se o cliente já usou o cupom em outra unidade do grupo, ele não consegue usar de novo aqui — e vice-versa."
            />
            <CartaoPonto
              icon={<StorefrontIcon />}
              titulo="Seus cupons continuam seus"
              texto="A rede não substitui nada: você segue criando e editando os cupons próprios desta loja, como já faz hoje."
            />
            <CartaoPonto
              icon={<LockOutlinedIcon />}
              titulo="Os da rede são só leitura"
              texto="Cupons da rede aparecem na sua lista marcados como “Rede”. Quem cria, edita e encerra é a matriz."
            />
          </Grade>
        </Box>
      </AoEntrar>

      <AoEntrar atraso={280}>
        <Paper
          variant="outlined"
          sx={{
            mt: { xs: 3, md: 4 },
            mb: 2,
            p: { xs: 2.5, md: 3 },
            borderColor: alpha(acento, 0.35),
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <CheckCircleOutlineIcon sx={{ color: acento, mt: '2px' }} />
            <Box>
              <Typography fontWeight={600} gutterBottom>
                Quer participar?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fale com quem administra a sua franquia e peça para incluir esta loja na rede do cardápio.
                Assim que isso for feito, a rede aparece aqui e os cupons dela passam a valer no seu
                cardápio — nenhuma configuração da sua parte.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </AoEntrar>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// B. A loja participa de uma rede
// ---------------------------------------------------------------------------

function ParticipaDaRede({
  nome,
  lojas,
}: {
  nome: string;
  lojas: { idConfiguracaoCardapio: number; nome: string }[];
}): JSX.Element {
  const tema = useTheme();
  const acento = tema.palette.primary.main;
  const escuro = tema.palette.mode === 'dark';
  const idConfig = useSelector((s: RootState) => s.adminAuth.idConfiguracaoSelecionada);

  return (
    <Box>
      <AoEntrar atraso={0}>
        <PainelDestaque>
          <Stack spacing={2} sx={{ maxWidth: 660 }}>
            <Chip
              size="small"
              icon={<CheckCircleOutlineIcon />}
              label="Esta loja participa"
              sx={{
                alignSelf: 'flex-start',
                bgcolor: alpha(acento, escuro ? 0.18 : 0.1),
                color: acento,
                fontWeight: 600,
                '& .MuiChip-icon': { color: acento },
              }}
            />
            <Stack direction="row" spacing={1.5} alignItems="center">
              <HubIcon sx={{ color: acento, fontSize: 32 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: 22, md: 28 } }}>
                {nome}
              </Typography>
            </Stack>
            <Typography color="text.secondary" sx={{ fontSize: { md: 17 } }}>
              Os cupons desta rede valem no seu cardápio automaticamente. Eles aparecem na tela de{' '}
              <strong>Cupons de desconto</strong> marcados como <em>Rede</em> — quem cria e edita é a matriz.
            </Typography>
          </Stack>
        </PainelDestaque>
      </AoEntrar>

      <AoEntrar atraso={140}>
        <Box sx={{ mt: { xs: 3, md: 4 } }}>
          <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 2 }}>
            <Typography variant="h6">Lojas participantes</Typography>
            <Typography variant="body2" color="text.secondary">
              ({lojas.length})
            </Typography>
          </Stack>
          <Paper variant="outlined">
            <Stack divider={<Divider />}>
              {lojas.map((l) => {
                const ehEsta = l.idConfiguracaoCardapio === idConfig;
                return (
                  <Stack
                    key={l.idConfiguracaoCardapio}
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ px: 2, py: 1.5, ...(ehEsta ? { bgcolor: alpha(acento, escuro ? 0.1 : 0.04) } : {}) }}
                  >
                    <PlaceIcon fontSize="small" sx={{ color: ehEsta ? acento : 'text.disabled' }} />
                    <Typography sx={{ flexGrow: 1 }} fontWeight={ehEsta ? 600 : 400}>
                      {l.nome}
                    </Typography>
                    {ehEsta && <Chip size="small" label="esta loja" sx={{ color: acento }} variant="outlined" />}
                  </Stack>
                );
              })}
              {lojas.length === 0 && (
                <Typography color="text.secondary" variant="body2" sx={{ p: 2 }}>
                  A matriz ainda não incluiu lojas nesta rede.
                </Typography>
              )}
            </Stack>
          </Paper>
        </Box>
      </AoEntrar>

      <AoEntrar atraso={280}>
        <Box sx={{ mt: { xs: 3, md: 4 }, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Como funcionam os cupons da rede
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Grade>
              <CartaoPonto
                icon={<LocalOfferIcon />}
                titulo="Aparecem sozinhos no seu cardápio"
                texto="Você não precisa cadastrar nem repassar nada. O cliente digita o código no checkout e o desconto é aplicado."
              />
              <CartaoPonto
                icon={<HubIcon />}
                titulo="Uma vez por cliente na rede toda"
                texto="Se o cliente já usou em outra loja da rede, aqui ele recebe um aviso de que o cupom já foi utilizado."
              />
              <CartaoPonto
                icon={<StorefrontIcon />}
                titulo="Pode haver teto por loja"
                texto="A matriz pode limitar quantos usos cada unidade consome. Atingido o teto, o cupom para de valer só aqui."
              />
              <CartaoPonto
                icon={<LockOutlinedIcon />}
                titulo="Somente leitura para você"
                texto="Na lista de cupons, os da rede não têm botão de editar nem de inativar — a campanha é gerida pela matriz."
              />
            </Grade>
          </Box>
        </Box>
      </AoEntrar>
    </Box>
  );
}
